<template>
  <div class="ibox form-page animated fadeInRightBig" v-if="$route.params.operation">
    <div class="ibox-title form-page__header">
      <div class="form-page__title-wrap">
        <h5 class="form-page__title">
          <i
            v-if="dataForm.title && dataForm.title.icon"
            :class="dataForm.title.icon"
            class="form-page__title-icon"
          ></i>
          {{ pageTitle }}
        </h5>
        <p class="form-page__subtitle" v-if="operationLabel">
          {{ operationLabel }}
        </p>
      </div>
    </div>

    <div class="ibox-content form-page__body">
      <form class="form-page__form" @submit.prevent="save" novalidate>
        <div class="row">
          <v-col
            :length="field.length || '12'"
            :offset="field.offset || null"
            v-for="(field, index) in dataForm.fields"
            :key="index"
          >
            <div
              class="form-group form-page__field"
              :class="{ 'has-error': !!fieldErrors[field.model] }"
            >
              <label :for="field.name" class="form-page__label">
                <span v-if="isRequired(field)" class="required" title="Obrigatório">*</span>
                {{ field.label }}
              </label>

              <!-- text / password / email / number / tel -->
              <input
                v-if="isEditable && isTextLike(field.type)"
                v-mask="field.mask ? field.mask : continuousMask(model[field.model])"
                :type="field.type === 'text' && field.inputType ? field.inputType : field.type"
                :class="controlClass(field)"
                :id="field.name"
                :name="field.name"
                v-model="model[field.model]"
                :placeholder="field.placeholder || field.label"
                :required="isRequired(field)"
                :disabled="saving || loading"
                :aria-invalid="!!fieldErrors[field.model]"
                @input="clearFieldError(field.model)"
              />

              <textarea
                v-else-if="isEditable && field.type === 'textarea'"
                :class="controlClass(field)"
                :id="field.name"
                :name="field.name"
                v-model="model[field.model]"
                :placeholder="field.placeholder || field.label"
                :required="isRequired(field)"
                :rows="field.rows || 3"
                :disabled="saving || loading"
                @input="clearFieldError(field.model)"
              ></textarea>

              <input
                v-else-if="isEditable && field.type === 'simple_date'"
                class="form-control"
                :class="{ 'is-invalid': !!fieldErrors[field.model] }"
                type="date"
                :id="field.name"
                :name="field.name"
                v-model="model[field.model]"
                :required="isRequired(field)"
                :disabled="saving || loading"
                @input="clearFieldError(field.model)"
              />

              <datetime
                v-else-if="isEditable && field.type === 'date'"
                v-model="model[field.model]"
                class="form-control along"
                :class="{ 'is-invalid': !!fieldErrors[field.model] }"
                :phrases="{ ok: 'Ok', cancel: 'Cancelar' }"
                value-zone="America/Sao_Paulo"
                zone="America/Sao_Paulo"
                :placeholder="field.placeholder || 'Selecione a data'"
                :label="field.label"
                :required="isRequired(field)"
                :id="field.name"
                :name="field.name"
                @input="clearFieldError(field.model)"
              />

              <select
                v-else-if="isEditable && field.type === 'select'"
                :class="controlClass(field)"
                :id="field.name"
                :name="field.name"
                v-model="model[field.model]"
                :required="isRequired(field)"
                :disabled="saving || loading"
                @change="clearFieldError(field.model)"
              >
                <option :value="null" disabled>
                  {{ field.placeholder || 'Selecione...' }}
                </option>
                <option
                  :value="item.id"
                  v-for="item in field.list"
                  :key="item.id"
                >
                  {{ field.list_label ? item[field.list_label] : item.name }}
                </option>
              </select>

              <input
                v-else-if="isEditable && field.type === 'file'"
                type="file"
                :class="controlClass(field)"
                :id="field.name"
                :name="field.name"
                :required="isRequired(field)"
                :disabled="saving || loading"
                @change="clearFieldError(field.model)"
              />

              <!-- view mode -->
              <template v-else-if="!isEditable">
                <span
                  v-if="field.type === 'password'"
                  class="form-page__readonly"
                >**********</span>
                <a
                  v-else-if="field.type === 'file' && model[field.model]"
                  :href="model[field.model]"
                  class="form-page__readonly-link"
                  target="_blank"
                  rel="noopener"
                >Abrir arquivo</a>
                <span
                  v-else-if="field.type === 'date' || field.type === 'simple_date'"
                  class="form-page__readonly"
                >{{ formatDate(model[field.model]) }}</span>
                <span v-else class="form-page__readonly">
                  {{ displayValue(field) }}
                </span>
              </template>

              <div
                v-if="fieldErrors[field.model]"
                class="invalid-feedback form-page__error"
              >
                {{ fieldErrors[field.model] }}
              </div>
              <small v-else-if="field.help" class="form-text text-muted">
                {{ field.help }}
              </small>
            </div>
          </v-col>
        </div>

        <div class="form-page__footer">
          <p class="form-page__required-hint" v-if="hasRequired && isEditable">
            <span class="required">*</span> Campos obrigatórios
          </p>
          <div class="form-page__actions">
            <router-link
              :to="dataForm.button.back"
              class="btn btn-return"
              :class="{ disabled: saving }"
            >
              <i class="fa fa-arrow-left"></i> Voltar
            </router-link>
            <button
              type="submit"
              class="btn btn-add"
              v-if="isEditable"
              :disabled="saving || loading"
            >
              <i :class="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import vCol from "../Utils/vCol";

export default {
  props: {
    dataForm: Object
  },
  components: {
    vCol
  },
  data() {
    return {
      model: {},
      fieldErrors: {},
      saving: false,
      loading: false
    };
  },
  watch: {
    "$route.params.id": function() {
      if (this.$route.params.id) {
        this.getModel();
      } else {
        this.model = {};
        this.fieldErrors = {};
      }
    }
  },
  mounted() {
    const self = this;
    if (self.$route.params.id) {
      self.getModel();
    } else {
      self.model = {};
    }

    const fields = self.dataForm.fields;
    if (fields) {
      fields.forEach(x => {
        if (x.isRelated) {
          this.$watch("model." + x.model, function(newValue) {
            x.value = newValue;
            self.$emit("fieldUpdated", x);
          });
        }
      });
    }
  },
  computed: {
    isEditable() {
      return this.$route.params.operation !== "view";
    },
    pageTitle() {
      const titles = (this.dataForm && this.dataForm.title) || {};
      return titles[this.$route.params.operation] || titles.label || "";
    },
    operationLabel() {
      const map = {
        add: "Preencha os dados para cadastrar",
        edit: "Atualize as informações e salve",
        view: "Somente visualização"
      };
      return map[this.$route.params.operation] || "";
    },
    hasRequired() {
      const fields = this.dataForm.fields || [];
      return fields.some(field => this.isRequired(field));
    }
  },
  methods: {
    isTextLike(type) {
      return ["text", "password", "email", "number", "tel"].includes(type);
    },
    controlClass(field) {
      return [
        "form-control",
        field.class || "",
        this.fieldErrors[field.model] ? "is-invalid" : ""
      ]
        .filter(Boolean)
        .join(" ");
    },
    clearFieldError(model) {
      if (this.fieldErrors[model]) {
        const next = Object.assign({}, this.fieldErrors);
        delete next[model];
        this.fieldErrors = next;
      }
    },
    displayValue(field) {
      if (field.type === "select" && field.list) {
        const item = field.list.find(
          i => String(i.id) === String(this.model[field.model])
        );
        if (item) {
          return field.list_label ? item[field.list_label] : item.name;
        }
      }
      return this.model[field.model] != null ? this.model[field.model] : "—";
    },
    formatDate(date) {
      if (!date) return "—";
      return moment(date).format("DD/MM/YYYY");
    },
    continuousMask(model) {
      let mask = "";
      if (model && model.length) {
        for (let i = 0; i < model.length; i++) {
          mask += "X";
        }
      }
      return mask;
    },
    isRequired(field) {
      if (this.$route.params.operation === "view") {
        return false;
      }
      return field.isRequired
        ? field.isRequired[this.$route.params.operation]
        : true;
    },
    getModel() {
      const self = this;
      const id = self.$route.params.id;
      const api = self.$store.state.api + self.dataForm.url.model + "/" + id;

      self.loading = true;
      $("#page_loader").show();

      self.$http
        .get(api)
        .then(response => {
          self.model = response.data.data[0];
          const fields = this.dataForm.fields || [];
          for (let i in fields) {
            if (fields[i].type === "password") {
              delete self.model[fields[i].model];
            }
          }
        })
        .catch(error => {
          const errors = (error.response && error.response.data) || "Erro ao carregar";
          self.$message(null, errors, "error");
        })
        .finally(() => {
          self.loading = false;
          $("#page_loader").hide();
        });
    },
    mapServerErrors(errors) {
      const fieldErrors = {};
      if (errors && typeof errors === "object" && !Array.isArray(errors)) {
        Object.keys(errors).forEach(key => {
          const value = errors[key];
          fieldErrors[key] = Array.isArray(value) ? value[0] : value;
        });
      }
      this.fieldErrors = fieldErrors;
    },
    save() {
      if (this.saving) return;

      const id = this.$route.params.id ? "/" + this.$route.params.id : "";
      const api = this.$store.state.api + this.dataForm.url.model + id;
      const self = this;
      const operation = self.$route.params.operation;

      self.fieldErrors = {};
      self.saving = true;
      $("#page_loader").show();

      const form = new FormData();
      for (var field in self.dataForm.fields) {
        const dataField = self.dataForm.fields[field];
        if (dataField.type === "file") {
          const fileUpload = document.getElementById(dataField.name);
          form.append(
            dataField.model,
            fileUpload.files[0] ? fileUpload.files[0] : ""
          );
        } else if (dataField.type === "date" && self.model[dataField.model]) {
          form.set(
            dataField.model,
            String(self.model[dataField.model]).substring(0, 10)
          );
        } else if (
          self.model[dataField.model] !== undefined &&
          self.model[dataField.model] !== null &&
          self.model[dataField.model] !== ""
        ) {
          form.set(dataField.model, self.model[dataField.model]);
        }
      }

      if (operation === "edit") {
        form.append("_method", "PUT");
        form.append("id", self.model.id);
      }

      self.$http
        .post(api, form)
        .then(() => {
          self.$message(
            "Sucesso",
            self.dataForm.messages.success[operation],
            "success"
          );
          this.$router.go(-1);
          self.model = {};
        })
        .catch(error => {
          const errors = (error.response && error.response.data) || {};
          self.mapServerErrors(errors);

          let message = errors;
          if (typeof errors === "object") {
            let html = "<ul>";
            $.each(errors, function(key, value) {
              html += "<li>" + (Array.isArray(value) ? value[0] : value) + "</li>";
            });
            html += "</ul>";
            message = html;
          }

          self.$message(null, message, "error");
        })
        .finally(() => {
          self.saving = false;
          $("#page_loader").hide();
        });
    }
  }
};
</script>

<style scoped>
.form-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.form-page__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #424241;
}

.form-page__title-icon {
  margin-right: 0.4rem;
  color: #03a830;
}

.form-page__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #777;
}

.form-page__label {
  display: block;
  font-weight: 600;
  font-size: 0.85rem;
  color: #424241;
  margin-bottom: 0.2rem;
}

.form-page__field {
  margin-bottom: 1rem;
}

.form-page__readonly,
.form-page__readonly-link {
  display: block;
  padding: 0.4rem 0;
  border-bottom: 1px solid #e5e5e5;
  color: #555;
  min-height: 1.75rem;
}

.form-page__readonly-link {
  color: #03a830;
  font-weight: 600;
}

.form-page__error {
  display: block;
  color: #bb0034;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-page__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e8e8e8;
}

.form-page__required-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #777;
}

.form-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
}

.form-page__actions .btn-add,
.form-page__actions .btn-return {
  float: none;
  min-width: 110px;
  padding: 0.35rem 0.85rem;
}
</style>



// WEBPACK FOOTER //
// src/components/Base/BaseForm.vue